import React, { useEffect, useMemo, useState } from 'react';
import Modal from './ModalTrue';
import {
  LenderBulkSubmissionDetails,
  LenderBulkSubmissionUpdatePayload,
} from '../api/claims';

import './LenderBulkSubmissionModal.css';

type FieldType = 'text' | 'number' | 'date' | 'textarea' | 'select';

type FieldConfig = {
  label: string;
  path: string;
  type?: FieldType;
  options?: Array<{ label: string; value: string }>;
};

type SectionConfig = {
  key: string;
  title: string;
  description?: string;
  fields: FieldConfig[];
};

type LenderBulkSubmissionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  details: LenderBulkSubmissionDetails | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onSave: (updates: LenderBulkSubmissionUpdatePayload) => Promise<void>;
  onRefresh: () => void;
};

const YES_NO_OPTIONS = [
  { label: 'Select...', value: '' },
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
  { label: 'Unknown', value: 'unknown' },
];

const SECTION_DEFINITIONS: SectionConfig[] = [
  {
    key: 'agreement',
    title: 'Agreement Basics',
    fields: [
      { label: 'Date of inception', path: 'agreement.dateOfInception', type: 'date' },
      { label: 'Date of settlement', path: 'agreement.dateOfSettlement', type: 'date' },
      { label: 'VIN', path: 'agreement.vin' },
      { label: 'Finance product type', path: 'agreement.financeProductType' },
      { label: 'Finance product code', path: 'agreement.financeProductCode' },
      { label: 'Agreement template reference', path: 'agreement.agreementTemplateReference' },
      { label: 'Dealer / retailer name', path: 'agreement.retailerName' },
      { label: 'Finance programme name', path: 'agreement.financeProgrammeName' },
    ],
  },
  {
    key: 'broker',
    title: 'Broker & Disclosure',
    fields: [
      { label: 'Was the broker independent?', path: 'broker.independentBroker', type: 'select', options: YES_NO_OPTIONS },
      { label: 'Commission disclosed?', path: 'broker.commissionDisclosureProvided', type: 'select', options: YES_NO_OPTIONS },
      { label: 'Evidence of disclosure', path: 'broker.commissionDisclosureEvidence', type: 'textarea' },
      { label: 'Subsequent disclosure', path: 'broker.subsequentDisclosure', type: 'textarea' },
    ],
  },
  {
    key: 'financials',
    title: 'Finance Summary',
    fields: [
      { label: 'Vehicle price', path: 'financials.vehiclePrice' },
      { label: 'Sale price', path: 'financials.salePrice' },
      { label: 'Deposit paid', path: 'financials.depositPaid' },
      { label: 'Credit advanced', path: 'financials.creditAdvanced' },
      { label: 'APR', path: 'financials.apr' },
      { label: 'Interest rate', path: 'financials.interestRate' },
      { label: 'Minimum interest rate available', path: 'financials.minInterestRate' },
      { label: 'Maximum interest rate available', path: 'financials.maxInterestRate' },
      { label: 'Evidence minimum rate not offered', path: 'financials.minRateNotOfferedEvidence', type: 'textarea' },
      { label: 'Monthly repayment amount', path: 'financials.monthlyRepaymentAmount' },
      { label: 'Length of monthly payments', path: 'financials.repaymentLength' },
      { label: 'Total amount payable', path: 'financials.totalAmountPayable' },
      { label: 'Balloon payment amount', path: 'financials.balloonPaymentAmount' },
      { label: 'Guaranteed future value', path: 'financials.guaranteedFutureValue' },
    ],
  },
  {
    key: 'commission',
    title: 'Commission Breakdown',
    fields: [
      { label: 'Commission paid to dealer', path: 'commission.dealerCommission' },
      { label: 'Commission paid to broker', path: 'commission.brokerCommission' },
      { label: 'Commission percentage', path: 'commission.commissionPercentage' },
      { label: 'Commission payment date', path: 'commission.commissionPaymentDate', type: 'date' },
      { label: 'Clawback / debit-back status', path: 'commission.commissionClawbackStatus' },
      { label: 'Commission structure (part 1)', path: 'commission.structurePart1', type: 'textarea' },
      { label: 'Commission structure (part 2)', path: 'commission.structurePart2', type: 'textarea' },
      { label: 'Commission structure (part 3)', path: 'commission.structurePart3', type: 'textarea' },
      { label: 'Commission structure (part 4)', path: 'commission.structurePart4', type: 'textarea' },
      { label: 'Additional incentives / bonuses', path: 'commission.additionalIncentives', type: 'textarea' },
      { label: 'Incentives recipient', path: 'commission.incentivesRecipient' },
    ],
  },
  {
    key: 'relationships',
    title: 'Commercial Relationship & Assessment',
    fields: [
      { label: 'Commercial tie between lender and broker', path: 'relationships.commercialTie', type: 'textarea' },
      { label: 'Suitability/affordability statement provided?', path: 'relationships.suitabilityStatementProvided', type: 'select', options: YES_NO_OPTIONS },
      { label: 'Affordability approval method', path: 'relationships.affordabilityApprovalMethod' },
      { label: 'Vulnerability/characteristics assessment', path: 'relationships.vulnerabilityAssessment', type: 'textarea' },
    ],
  },
  {
    key: 'ancillaryProducts',
    title: 'Ancillary Products',
    fields: [
      { label: 'GAP insurance cost', path: 'ancillaryProducts.gap.cost' },
      { label: 'GAP provider', path: 'ancillaryProducts.gap.provider' },
      { label: 'GAP commission', path: 'ancillaryProducts.gap.commission' },
      { label: 'PPI/Shortfall amount', path: 'ancillaryProducts.ppi.amount' },
      { label: 'PPI/Shortfall provider', path: 'ancillaryProducts.ppi.provider' },
      { label: 'PPI/Shortfall commission', path: 'ancillaryProducts.ppi.commission' },
      { label: 'Extended warranty amount', path: 'ancillaryProducts.extendedWarranty.amount' },
      { label: 'Extended warranty provider', path: 'ancillaryProducts.extendedWarranty.provider' },
      { label: 'Extended warranty commission', path: 'ancillaryProducts.extendedWarranty.commission' },
      { label: 'Service / maintenance cost', path: 'ancillaryProducts.serviceMaintenance.cost' },
      { label: 'Service / maintenance provider', path: 'ancillaryProducts.serviceMaintenance.provider' },
      { label: 'Service / maintenance commission', path: 'ancillaryProducts.serviceMaintenance.commission' },
      { label: 'Paint & fabric insurance cost', path: 'ancillaryProducts.paintFabric.cost' },
      { label: 'Paint & fabric insurance provider', path: 'ancillaryProducts.paintFabric.provider' },
      { label: 'Paint & fabric insurance commission', path: 'ancillaryProducts.paintFabric.commission' },
    ],
  },
  {
    key: 'extras',
    title: 'Extras & Follow-up',
    fields: [
      { label: 'Fuel/petrol contribution added', path: 'extras.fuelContribution' },
      { label: 'Cashback incentives', path: 'extras.cashbackIncentives' },
      { label: 'Contacted consumer after complaint?', path: 'followUp.contactedConsumer', type: 'select', options: YES_NO_OPTIONS },
      { label: 'Duplicate claim?', path: 'followUp.duplicateClaim', type: 'select', options: YES_NO_OPTIONS },
      { label: 'Duplicate claim evidence', path: 'followUp.duplicateEvidence', type: 'textarea' },
    ],
  },
];

const cloneDetails = (details: LenderBulkSubmissionDetails | null): LenderBulkSubmissionDetails | null => {
  if (!details) return null;
  return JSON.parse(JSON.stringify(details));
};

const getValueAtPath = (details: LenderBulkSubmissionDetails | null, path: string): string => {
  if (!details) return '';
  return path.split('.').reduce<any>((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ''), details) ?? '';
};

const setValueAtPath = (
  details: LenderBulkSubmissionDetails,
  path: string,
  value: string | null
): LenderBulkSubmissionDetails => {
  const clone = cloneDetails(details);
  if (!clone) return details;
  const keys = path.split('.');
  let cursor: any = clone;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    if (cursor[key] === undefined || cursor[key] === null) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  });
  return clone;
};

const buildDiff = (
  original: LenderBulkSubmissionDetails | null,
  edited: LenderBulkSubmissionDetails | null,
  excludeMeta = true
): LenderBulkSubmissionUpdatePayload => {
  if (!edited) {
    return {};
  }

  const diffRecursive = (base: any, updated: any): any => {
    const diff: Record<string, any> = {};
    Object.keys(updated ?? {}).forEach((key) => {
      if (excludeMeta && key === 'meta') {
        return;
      }
      const updatedValue = updated[key];
      const baseValue = base ? base[key] : undefined;
      if (updatedValue && typeof updatedValue === 'object' && !Array.isArray(updatedValue)) {
        const nested = diffRecursive(baseValue || {}, updatedValue);
        if (Object.keys(nested).length > 0) {
          diff[key] = nested;
        }
      } else if (updatedValue !== baseValue) {
        diff[key] = updatedValue;
      }
    });
    return diff;
  };

  return diffRecursive(original || {}, edited);
};

const LenderBulkSubmissionModal: React.FC<LenderBulkSubmissionModalProps> = ({
  isOpen,
  onClose,
  details,
  loading,
  saving,
  error,
  onSave,
  onRefresh,
}) => {
  const [draft, setDraft] = useState<LenderBulkSubmissionDetails | null>(null);
  const [baseline, setBaseline] = useState<LenderBulkSubmissionDetails | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-active');
    } else {
      document.body.classList.remove('modal-active');
    }

    return () => {
      document.body.classList.remove('modal-active');
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && details) {
      setDraft(cloneDetails(details));
      setBaseline(cloneDetails(details));
      setLocalError(null);
    }
  }, [isOpen, details]);

  const diff = useMemo(() => buildDiff(baseline, draft), [baseline, draft]);
  const hasChanges = useMemo(() => Object.keys(diff || {}).length > 0, [diff]);

  const handleChange = (field: FieldConfig, value: string) => {
    if (!draft) return;
    const normalized = value === '' ? null : value;
    const updated = setValueAtPath(draft, field.path, normalized);
    setDraft(updated);
  };

  const handleSave = async () => {
    if (!hasChanges || !draft) {
      return;
    }
    setLocalError(null);
    try {
      await onSave(diff);
    } catch (err: any) {
      setLocalError(err?.message || 'Failed to save lender bulk submission details');
      return;
    }
  };

  const handleReset = () => {
    setDraft(cloneDetails(baseline));
    setLocalError(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal onClose={onClose} variant="fullscreen" contentClassName="lender-bulk-modal-container">
      <div className="lender-bulk-modal">
        <header className="lender-bulk-modal__header">
          <div>
            <h2>Lender Bulk Submission details</h2>
            {details?.meta && (
              <p className="lender-bulk-modal__meta">
                Last updated {details.meta.lastUpdatedAt ? new Date(details.meta.lastUpdatedAt).toLocaleString() : '—'}
                {details.meta.lastUpdatedBy ? ` by ${details.meta.lastUpdatedBy}` : ''}
                {details.meta.version ? ` · Version ${details.meta.version}` : ''}
              </p>
            )}
          </div>
          <div className="lender-bulk-modal__actions">
            <button type="button" onClick={onRefresh} disabled={loading || saving}>
              Refresh
            </button>
            <button type="button" onClick={handleReset} disabled={!hasChanges || saving}>
              Reset changes
            </button>
            <button
              type="button"
              className="primary"
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </header>

        {(loading && !details) ? (
          <div className="lender-bulk-modal__state">Loading lender bulk submission details…</div>
        ) : (
          <div className="lender-bulk-modal__body">
            {error && <div className="lender-bulk-modal__error">{error}</div>}
            {localError && <div className="lender-bulk-modal__error">{localError}</div>}

            {SECTION_DEFINITIONS.map((section) => (
              <section key={section.key} className="lender-bulk-modal__section">
                <h3>{section.title}</h3>
                {section.description && <p className="lender-bulk-modal__section-desc">{section.description}</p>}
                <div className="lender-bulk-modal__grid">
                  {section.fields.map((field) => {
                    const fieldValue = getValueAtPath(draft, field.path);
                    const inputId = `${field.path.replace(/\./g, '-')}`;
                    return (
                      <label key={field.path} className="lender-bulk-modal__field" htmlFor={inputId}>
                        <span>{field.label}</span>
                        {field.type === 'textarea' ? (
                          <textarea
                            id={inputId}
                            value={fieldValue ?? ''}
                            onChange={(event) => handleChange(field, event.target.value)}
                            rows={3}
                            disabled={saving}
                          />
                        ) : field.type === 'select' ? (
                          <select
                            id={inputId}
                            value={fieldValue ?? ''}
                            onChange={(event) => handleChange(field, event.target.value)}
                            disabled={saving}
                          >
                            {(field.options || YES_NO_OPTIONS).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={inputId}
                            type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                            value={fieldValue ?? ''}
                            onChange={(event) => handleChange(field, event.target.value)}
                            disabled={saving}
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LenderBulkSubmissionModal;
